import React, { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Select } from 'components/forms/Select';
import {
  SelectItem,
  SelectItemTemplate,
  SelectValueTemplate,
  SelectViewProps
} from 'components/forms/Select/Select.view';
import { FormOptions } from 'constants/form';
import { useForm } from 'react-hook-form';
import classes from './CustomPublishingRow.module.scss';
import { GetCategoriesQuery } from 'models/graphql';
import {
  GetQueueMetadata_workspace_queueMeta,
  GetSocialProfiles_workspace_socialProfiles
} from '../../../../models/legacy-graphql';
import { PauseStates } from '../Pause.controller';
import { ListItem } from 'components/actions/ListItem';
import { FormElementAction } from '../../../../models/basic-objects';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import { useDimensions } from '../../../../hooks/useDimensions';

export type CustomPublishingRowProps = {
  noBorder?: boolean;
  submitted: boolean;
  children: React.ReactNode;
  category?: GetCategoriesQuery['workspace']['categories'][number];
  profile?: GetSocialProfiles_workspace_socialProfiles;
  queueMeta: GetQueueMetadata_workspace_queueMeta[];
  action: FormElementAction;
  onCustomize: VoidFunction;
  id: string;
};
export type CustomPublishingRowDropdownState = PauseStates | 'Mixed';

export const CustomPublishingRowView = ({
  children,
  noBorder,
  submitted,
  category,
  profile,
  queueMeta,
  action,
  onCustomize,
  id
}: CustomPublishingRowProps): JSX.Element => {
  const documentRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(documentRef);
  const value = useMemo((): CustomPublishingRowDropdownState => {
    const items = queueMeta.filter(
      item => item.category?.id === category?.id || item.socialProfile?.id === profile?.id
    );
    if (items.every(item => item.paused)) {
      return PauseStates.Paused;
    }
    if (items.some(item => item.paused)) {
      return 'Mixed';
    } else {
      return PauseStates.Active;
    }
  }, [queueMeta, category, profile]);
  const useFormInstance = useForm({
    ...FormOptions
  });
  const valueTemplate: SelectValueTemplate = (props: SelectViewProps, globalClasses: string) => {
    return (
      <div
        className={classNames(globalClasses, classes.SelectValue)}
        onClick={() => {
          props.setExpanded(!props.expanded);
        }}>
        <div>
          <div
            className={classNames(classes.ColoredCircle, {
              [classes.Fill]: value === PauseStates.Active,
              [classes.HalfFill]: value === 'Mixed'
            })}>
            <div />
          </div>
          <span>{value}</span>
        </div>
      </div>
    );
  };
  const itemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    return (
      <ListItem
        key={index}
        role={'option'}
        aria-label={item.label}
        variation={'regular'}
        type={'li'}
        selected={item.value === value}
        action={() => {
          if (item.value === 'Customize') {
            onCustomize();
            props.setExpanded(false);
          } else {
            props.updateValue(item.value);
          }
        }}
        disabled={submitted}>
        {item.label}
      </ListItem>
    );
  };

  const contentCellClasses = classNames('cell', {
    auto: width >= 600,
    'small-12': width < 600
  });
  const actionCellClasses = classNames('cell', {
    shrink: width >= 600,
    'small-12 margin-top-small-8': width < 600
  });

  return (
    <div
      className={classNames(
        classes.CustomPublishingRow,
        { 'no-b-bor': noBorder },
        'grid-x grid-padding-x align-justify'
      )}>
      <div className={contentCellClasses}>{children}</div>
      <div className={actionCellClasses}>
        <div className={classNames('grid-x', classes.RowContainer)}>
          <div className={classes.SelectorContainer}>
            <Select
              items={[
                { label: PauseStates.Active, value: PauseStates.Active },
                { label: PauseStates.Paused, value: PauseStates.Paused }
              ]}
              name={`catgory-${id}`}
              size={'medium'}
              rules={{}}
              action={action}
              selectedValue={value}
              useFormInstance={useFormInstance}
              valueTemplate={valueTemplate}
              itemTemplate={itemTemplate}
            />
          </div>

          <div className={classes.CustomizeContainer}>
            <Button
              type={'button'}
              variant={'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              icon={{ side: 'center', value: IconsEnum.settings }}
              action={onCustomize}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
